<script setup lang="ts">
import { SquareCheck, Trash2 } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import EmptyState from '@/components/shared/EmptyState.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import type { DocumentTaskItem } from './types';

const props = defineProps<{
    tasks: DocumentTaskItem[];
    currentUserId: string;
}>();

const emit = defineEmits<{
    add: [body: string];
    toggle: [id: string];
    remove: [id: string];
}>();

const draft = ref('');

function submit() {
    const body = draft.value.trim();

    if (!body) {
        return;
    }

    emit('add', body);
    draft.value = '';
}

const pending = computed(() => props.tasks.filter((t) => !t.completed_at));
const done = computed(() => props.tasks.filter((t) => t.completed_at));
</script>

<template>
    <div class="flex h-full flex-col">
        <div class="flex-1 space-y-4 p-4">
            <EmptyState
                v-if="props.tasks.length === 0"
                :icon="SquareCheck"
                title="Aucune tâche"
                description="Listez ici les actions à mener sur ce document."
                class="border-border/40"
            />

            <div v-if="pending.length > 0" class="space-y-1.5">
                <p
                    v-if="done.length > 0"
                    class="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase"
                >
                    À faire
                </p>
                <div
                    v-for="task in pending"
                    :key="task.id"
                    class="group flex items-start gap-2 rounded-lg border border-border/50 bg-muted/20 p-2.5"
                >
                    <Checkbox
                        :model-value="false"
                        class="mt-0.5"
                        @update:model-value="emit('toggle', task.id)"
                    />
                    <div class="min-w-0 flex-1">
                        <p class="text-sm text-foreground">{{ task.body }}</p>
                        <p class="mt-0.5 text-[11px] text-muted-foreground">
                            {{ task.author.name }}
                        </p>
                    </div>
                    <button
                        v-if="task.author.id === props.currentUserId"
                        class="shrink-0 rounded p-0.5 text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                        title="Supprimer la tâche"
                        @click="emit('remove', task.id)"
                    >
                        <Trash2 class="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>

            <div v-if="done.length > 0" class="space-y-1.5">
                <p
                    class="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase"
                >
                    Terminé
                </p>
                <div
                    v-for="task in done"
                    :key="task.id"
                    class="group flex items-start gap-2 rounded-lg border border-border/30 p-2.5 opacity-60"
                >
                    <Checkbox
                        :model-value="true"
                        class="mt-0.5"
                        @update:model-value="emit('toggle', task.id)"
                    />
                    <div class="min-w-0 flex-1">
                        <p class="text-sm text-foreground line-through">
                            {{ task.body }}
                        </p>
                        <p class="mt-0.5 text-[11px] text-muted-foreground">
                            Coché par {{ task.completed_by?.name }}
                        </p>
                    </div>
                    <button
                        v-if="task.author.id === props.currentUserId"
                        class="shrink-0 rounded p-0.5 text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                        title="Supprimer la tâche"
                        @click="emit('remove', task.id)"
                    >
                        <Trash2 class="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>

        <div class="shrink-0 border-t border-border/40 p-3">
            <div class="flex items-center gap-2">
                <Input
                    v-model="draft"
                    placeholder="Nouvelle tâche…"
                    class="h-8 text-sm"
                    @keydown.enter.prevent="submit"
                />
                <Button
                    size="sm"
                    class="h-8 shrink-0 px-3 text-xs"
                    :disabled="!draft.trim()"
                    @click="submit"
                >
                    Ajouter
                </Button>
            </div>
        </div>
    </div>
</template>
